/**
 * Dumb view component that renders both dropdowns (source + entity).
 * Uses the SandboxViewer Autocomplete directly; no fetching or store access.
 */

import React from "react";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "SandboxViewerComponent/src/libs/Autocomplete";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

const selectContainerStyle = {
  width: "100%",
  boxSizing: "border-box",
  paddingRight: "34px",
  paddingLeft: "5px",
  marginBottom: "20px",
};

function renderHighlightedOption(getLabel, selectedLabel) {
  return function renderOption(props, option, { inputValue }) {
    const label = getLabel(option);
    // When the dropdown reopens after a selection, inputValue equals the selected
    // option's label. In that state we don't want to highlight anything.
    const effectiveInputValue = inputValue === selectedLabel ? "" : inputValue;
    const matches = match(label, effectiveInputValue, { insideWords: true });
    const parts = parse(label, matches);
    return (
      <li {...props}>
        {parts.map((part, index) =>
          part.highlight ? (
            <strong key={index} style={{ fontWeight: 700 }}>
              {part.text}
            </strong>
          ) : (
            <span key={index}>{part.text}</span>
          )
        )}
      </li>
    );
  };
}

export default function RegionDropdowns({
  map,
  view,

  services,
  loadingServices,
  selectedService,
  onSelectService,

  items,
  loadingItems,
  selectedItem,
  onSelectItem,

  nls,
}) {
  if (!map || !view) return null;

  return (
    <>
      <div style={selectContainerStyle} data-cy="service-selector">
        <Autocomplete
          map={map}
          view={view}
          renderInput={({ inputProps, ...params }) => (
            <TextField
              variant="standard"
              {...params}
              inputProps={{ ...inputProps }}
              fullWidth
              placeholder={loadingServices ? nls.loadingServices : nls.source}
              // value={selectedService?.Name || ""}
            />
          )}
          options={services || []}
          getOptionLabel={(option) => option?.Name || ""}
          renderOption={renderHighlightedOption(
            (option) => option?.Name || "",
            selectedService?.Name || ""
          )}
          isOptionEqualToValue={(option, value) =>
            option?.Id != null && option.Id === value?.Id
          }
          value={selectedService || null}
          disabled={Boolean(loadingServices) || (services || []).length === 0}
          disableClearable
          fullWidth
          onChange={onSelectService}
          loading={Boolean(loadingServices)}
        />
      </div>

      {selectedService && (
        <div style={selectContainerStyle} data-cy="region-selector">
          <Autocomplete
            map={map}
            view={view}
            renderInput={({ inputProps, InputProps, ...params }) => (
              <TextField
                variant="standard"
                {...params}
                InputProps={{
                  ...InputProps,
                  endAdornment: (
                    <>
                      {loadingItems ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : null}
                      {InputProps?.endAdornment}
                    </>
                  ),
                }}
                inputProps={{ ...inputProps }}
                fullWidth
                placeholder={loadingItems ? nls.loadingItems : nls.entity}
                // value={selectedItem?.displayLabel || ""}
              />
            )}
            options={items || []}
            getOptionLabel={(option) => option?.displayLabel || ""}
            renderOption={renderHighlightedOption(
              (option) => option?.displayLabel || "",
              selectedItem?.displayLabel || ""
            )}
            isOptionEqualToValue={(option, value) =>
              option?.ObjectId != null && option.ObjectId === value?.ObjectId
            }
            value={selectedItem || null}
            disabled={Boolean(loadingItems)}
            disableClearable
            fullWidth
            onChange={onSelectItem}
            loading={Boolean(loadingItems)}
          />
        </div>
      )}
    </>
  );
}
